import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'
import TextareaAutosize from 'react-textarea-autosize'

import { FramesManager } from 'shared-libs/components/view/frames-manager'
import type { UI2TextAreaFieldProps } from 'shared-libs/components/ui2/fields/ui2-text-area-field/ui2-text-area-field-props'
import { UI2_TEXT_AREA_FIELD_DEFAULT_PROPS } from 'shared-libs/components/ui2/fields/ui2-text-area-field/ui2-text-area-field-props'
import { translateString } from 'shared-libs/helpers/utils'

import { UI2FieldControl } from 'browser/components/ui2/fields/field-ui/field-control/field-control'
import { UI2FieldShell, IUI2FieldShellProps } from '../field-ui/field-shell/field-shell'
import { NO_VALUE_AVAILABLE_TEXT, UI2NoValueAvailable } from '../field-ui/no-value-available/no-value-available'

import './_ui2-text-area-field.scss'

interface IUI2TextAreaFieldWebExtras {
  onChange?: (value: any) => void
  errors?: any[]
  frames?: FramesManager
  translationTable?: any
  className?: string
  testID?: string
  accessibilityLabel?: string
  isLoading?: boolean
}

/**
 * @uiComponent
 */
export interface IUI2TextAreaFieldProps extends UI2TextAreaFieldProps, IUI2TextAreaFieldWebExtras {}

interface IUI2TextAreaFieldState {
  internalValue: string
}

export class UI2TextAreaField extends React.Component<IUI2TextAreaFieldProps, IUI2TextAreaFieldState> {
  public static defaultProps = {
    ...UI2_TEXT_AREA_FIELD_DEFAULT_PROPS,
    rows: 4, // web-specific: DW uses 4-row default; mobile uses 3 (shared constant)
  }

  constructor(props: IUI2TextAreaFieldProps) {
    super(props)
    this.state = {
      internalValue: _.isNil(props.value) ? '' : String(props.value),
    }
  }

  public componentDidUpdate(prevProps: IUI2TextAreaFieldProps) {
    if (this.props.value !== prevProps.value) {
      this.setState({ internalValue: _.isNil(this.props.value) ? '' : String(this.props.value) })
    }
  }

  public render() {
    const { className } = this.props
    return (
      <UI2FieldShell
        {...(this.props as unknown as IUI2FieldShellProps)}
        className={classNames('ui2-textAreaField', className)}
        renderReadOnly={this.renderReadOnlyValue}
        // Body is a UI2FieldControl, which slots Cancel beside the input.
        bodyIsFieldControl={true}
      >
        <UI2FieldControl className='ui2-textAreaField-control'>
          {this.renderTextarea()}
        </UI2FieldControl>
      </UI2FieldShell>
    )
  }

  private renderReadOnlyValue = (value: string | undefined, { editNode }: { editNode?: React.ReactNode }): React.ReactNode => {
    const content = (_.isNil(value) || value === '')
      ? <UI2NoValueAvailable />
      : <span className='ui2-textAreaField-readOnlyValue'>{String(value)}</span>
    return (
      <>
        <div className='ui2-fieldShell-readOnly-content'>{content}</div>
        {editNode}
      </>
    )
  }

  private renderTextarea(): React.ReactNode {
    const {
      autoGrow, rows, maxRows, isDisabled, isLoading, placeholder,
      translationTable, frames, testID, accessibilityLabel, autoFocus, label = "",
    } = this.props
    const { internalValue } = this.state

    const table = Object.assign({}, translationTable, frames?.getContext('translationTable'))
    const translatedPlaceholder = placeholder
      ? translateString(placeholder, table)
      : translateString(label, table)
    const effectivePlaceholder = (!isDisabled && !isLoading)
      ? (translatedPlaceholder ?? '')
      : NO_VALUE_AVAILABLE_TEXT
    const effectiveMinRows = rows ?? 4
    const effectiveMaxRows = autoGrow === false ? effectiveMinRows : maxRows

    return (
      <TextareaAutosize
        autoFocus={autoFocus}
        disabled={isDisabled || isLoading}
        minRows={effectiveMinRows}
        maxRows={effectiveMaxRows}
        onChange={this.handleChange}
        placeholder={effectivePlaceholder}
        value={internalValue}
        data-testid={testID}
        aria-label={accessibilityLabel}
      />
    )
  }

  private handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value
    this.setState({ internalValue: newValue })
    this.props.onChange?.(newValue)
  }
}
